import React, { useState, useEffect } from "react";
import { useAction } from "wasp/client/operations";
import { createJob } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Briefcase,
  DollarSign,
  Phone,
  Save,
  AlertCircle,
  Clock,
  Mail,
  FileText,
  Star,
  Plus,
  X,
  Calendar,
} from "lucide-react";

interface JobFormData {
  title: string;
  description: string;
  listingType: "OFFER" | "REQUEST";
  price: string;
  priceType: "HOURLY" | "FIXED" | "PER_SESSION" | "FREE";
  schedule: string;
  contactEmail: string;
  contactPhone: string;
}

const LISTING_TYPE_OPTIONS = [
  { value: "OFFER", label: "Offering", icon: Briefcase },
  { value: "REQUEST", label: "Requesting", icon: Star },
];

const PRICE_TYPE_OPTIONS = [
  { value: "HOURLY", label: "Hourly" },
  { value: "FIXED", label: "Fixed" },
  { value: "PER_SESSION", label: "Per Session" },
  { value: "FREE", label: "Free" },
];

export default function AddJobPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const createJobFn = useAction(createJob);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<JobFormData>({
    title: "",
    description: "",
    listingType: "OFFER",
    price: "",
    priceType: "FIXED",
    schedule: "",
    contactEmail: user?.email || "",
    contactPhone: user?.phoneNumber || "",
  });

  useEffect(() => {
    if (!user) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      contactEmail: prev.contactEmail || user.email || "",
      contactPhone: prev.contactPhone || user.phoneNumber || "",
    }));
  }, [user]);

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to post jobs.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const formatPhoneNumber = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (field: keyof JobFormData, value: any) => {
    if (field === "contactPhone") {
      value = formatPhoneNumber(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = "Job title is required";
    if (!formData.description.trim()) newErrors.description = "Job description is required";
    if (formData.priceType !== "FREE" && !formData.price) newErrors.price = "Price is required";
    if (!selectedLocation?.id) newErrors.location = "No location selected";
    if (formData.contactPhone && formData.contactPhone?.replace(/\D/g, "").length < 10) {
      newErrors.contactPhone = "Please enter a valid 10-digit phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await createJobFn({
        locationId: selectedLocation!.id,
        title: formData.title.trim(),
        description: formData.description.trim(),
        listingType: formData.listingType,
        price: formData.priceType === "FREE" ? 0 : parseFloat(formData.price),
        priceType: formData.priceType,
        schedule: formData.schedule.trim(),
        email: formData.contactEmail || undefined,
        phone: formData.contactPhone || undefined,
        visibility: "VERIFIED",
      });

      if (selectedLocation?.locationCode) {
        navigate(`/locations/${selectedLocation.locationCode}/jobs`);
      } else {
        navigate("/jobs");
      }
    } catch (error) {
      console.error("Error creating job:", error);
      setErrors({ submit: "Failed to create job. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="job-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Posting..." : "Post Job"}
      </ActionButton>
      <ActionButtonSecondary onClick={() => navigate(-1)}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Information" hideOnMobile>
        <div className="space-y-4">
          <div className="flex gap-3 text-sm">
            <div className="mt-1 p-1 bg-blue-50 rounded-md text-blue-600">
              <AlertCircle className="w-4 h-4" />
            </div>
            <p className="text-slate-600">
              Your job will be visible to students in <strong>{selectedLocation?.name}</strong>.
            </p>
          </div>
          <div className="flex gap-3 text-sm">
            <div className="mt-1 p-1 bg-amber-50 rounded-md text-amber-600">
              <Star className="w-4 h-4" />
            </div>
            <p className="text-slate-600">
              Include specific details about requirements and schedule.
            </p>
          </div>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Post a Job"
      subtitle={selectedLocation?.name || "Select a location"}
      icon={Briefcase}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="job-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Listing Type & Title */}
        <div className="space-y-6">
          <div className="space-y-3">
            <label className="text-sm font-semibold text-slate-900 ml-1">
              Purpose
            </label>
            <div className="inline-flex p-1 bg-slate-100 rounded-xl w-full max-w-md">
              {LISTING_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleChange("listingType", opt.value)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    formData.listingType === opt.value
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <opt.icon className="w-4 h-4" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700 ml-1">Job Title</label>
            <div className="relative group">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="e.g. Graphic Designer, Math Tutor, Delivery Help"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                required
              />
            </div>
          </div>
        </div>

        {/* Pricing & Schedule */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-900 border-b border-slate-100 pb-2">
            <DollarSign className="w-4 h-4 text-blue-600" />
            <span className="font-semibold">Compensation & Schedule</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700 ml-1">Payment Type</label>
              <div className="inline-flex p-1 bg-slate-100 rounded-xl w-full">
                {PRICE_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleChange("priceType", opt.value)}
                    className={`flex-1 py-2 px-1 rounded-lg text-xs font-semibold transition-all ${
                      formData.priceType === opt.value
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5 pt-1.5">
              <label className="text-sm font-medium text-slate-700 ml-1">Amount</label>
              <div className="relative group">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  disabled={formData.priceType === "FREE"}
                  onChange={(e) => handleChange("price", e.target.value)}
                  placeholder={formData.priceType === "FREE" ? "No charge" : "0.00"}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all disabled:opacity-50 disabled:bg-slate-100"
                />
              </div>
              {errors.price && <p className="text-xs text-rose-500 mt-1 ml-1">{errors.price}</p>}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700 ml-1">Typical Schedule / Time Commit</label>
            <div className="relative group">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                value={formData.schedule}
                onChange={(e) => handleChange("schedule", e.target.value)}
                placeholder="e.g. Weekends only, 5-10 hours/week, or Flexible"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
              />
            </div>
          </div>
        </div>

        {/* Job Description */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-900 border-b border-slate-100 pb-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <span className="font-semibold">Job Details</span>
          </div>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            placeholder="Provide a detailed description of the job, requirements, and what you're looking for..."
            rows={5}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none"
            required
          />
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-900 border-b border-slate-100 pb-2">
            <Mail className="w-4 h-4 text-blue-600" />
            <span className="font-semibold">Contact Information</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 ml-1">Email</label>
              <div className="relative bg-slate-100 rounded-xl cursor-not-allowed">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={formData.contactEmail}
                  readOnly
                  disabled
                  className="w-full pl-10 pr-4 py-2.5 bg-transparent border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed outline-none"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 ml-1">Phone Number (Optional)</label>
              <div className="relative group">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => handleChange("contactPhone", e.target.value)}
                  placeholder="(613) 000-0000"
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                />
              </div>
              {errors.contactPhone && (
                <p className="text-xs text-rose-500 mt-1 ml-1">{errors.contactPhone}</p>
              )}
            </div>
          </div>
        </div>

        {errors.submit && (
          <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-3 text-rose-600">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">{errors.submit}</p>
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
